"use client";

import type React from "react";
import { useEffect, useRef } from "react";
import {
  resolveProfileLink,
  sortProfileLinks
} from "@/lib/link-types";
import { buildProfileRenderModel } from "@/lib/render-model";
import { analyzeThemeSection } from "@/lib/theme-structure";
import { normalizeThemeId } from "@/lib/themes";
import type { CompiledMarkdownDocument, ProfileContent, ProfileLink, ThemeId } from "@/lib/types";
import {
  InteractiveLinkBasePathProvider,
  LinkActionIcon,
  ProfileLinkItem,
  ThemeAttribution,
  ThemeFooter,
  ThemeSectionBody,
  type ThemeRendererProps
} from "./themes/theme-primitives";
import { ArcadeTheme, NoiseTheme, PixelTheme, RawTheme } from "./themes/ExperimentalThemes";
import { BentoTheme, BlueprintTheme, ExhibitTheme, FolioTheme, InkTheme, LetterTheme } from "./themes/StructuredThemes";
import { TerminalLinksTree, TerminalSession } from "./themes/TerminalSession";
import { useI18n } from "./I18nProvider";

type ThemePreviewProps = {
  profile: ProfileContent;
  interactiveLinks?: boolean;
  showAttribution?: boolean;
  onLinkClick?: (link: ProfileLink) => void;
  linkRedirectBasePath?: string;
  compiledMarkdown?: CompiledMarkdownDocument;
};

const THEME_RENDERERS: Record<ThemeId, (props: ThemeRendererProps) => React.ReactElement> = {
  paper: JournalTheme,
  poster: SignalTheme,
  index: MosaicTheme,
  riso: RisoTheme,
  prism: PrismTheme,
  field: FieldTheme,
  noir: NoirTheme,
  terminal: TerminalTheme,
  chrome: RawTheme,
  pixel: PixelTheme,
  arcade: ArcadeTheme,
  noise: NoiseTheme,
  folio: FolioTheme,
  ink: InkTheme,
  bento: BentoTheme,
  letter: LetterTheme,
  exhibit: ExhibitTheme,
  blueprint: BlueprintTheme
};

export function ThemePreview({
  profile,
  interactiveLinks = false,
  showAttribution = true,
  onLinkClick,
  linkRedirectBasePath,
  compiledMarkdown
}: ThemePreviewProps) {
  const { t } = useI18n();
  const themeId = normalizeThemeId(profile.themeId);
  const normalizedProfile = profile.themeId === themeId ? profile : { ...profile, themeId };
  const model = buildProfileRenderModel(normalizedProfile, compiledMarkdown);
  const Renderer = THEME_RENDERERS[themeId] ?? JournalTheme;

  return (
    <InteractiveLinkBasePathProvider basePath={linkRedirectBasePath}>
      <Renderer model={model} interactiveLinks={interactiveLinks} showAttribution={showAttribution} onLinkClick={onLinkClick} t={t} />
    </InteractiveLinkBasePathProvider>
  );
}

/* --- THEMES --- */

function JournalTheme({ model, interactiveLinks, onLinkClick, showAttribution, t }: ThemeRendererProps) {
  const links = sortProfileLinks(model.profile.links);
  const sparseClass = model.sections.length === 0 && links.length === 0 ? " theme-v2-sparse" : "";
  const rawName = (model.profile.displayName || "").trim();
  const monogramInitial = Array.from(rawName)[0]?.toUpperCase() || "J";

  return (
    <article className={`theme-v2-wrapper theme-v2-journal${sparseClass}`} data-theme="paper">
      <div className="journal-sheet">
        <div className="journal-texture" aria-hidden="true" />
        <header className="journal-header">
          <div className="journal-folio">
            <span>30S / JOURNAL · 01</span>
            <strong>30s.to/@{model.profile.handle || "handle"}</strong>
          </div>
          <div className="journal-cover">
            <div className="journal-identity">
              <span className="journal-kicker" aria-hidden="true">A thirty-second field note</span>
              <h1>{model.profile.displayName || t("theme.fallback.name")}</h1>
              <p>{model.profile.oneLineIdentity || t("theme.fallback.identity")}</p>
            </div>
            <div className="journal-monogram" aria-hidden="true">
              <strong>{monogramInitial}</strong>
              <span>ISSUE<br />001</span>
            </div>
          </div>
        </header>
        {model.profile.tags.length > 0 && (
          <div className="journal-tags">
            {model.profile.tags.map(tag => <span key={tag}>{tag}</span>)}
          </div>
        )}
        <div className="journal-flow">
          <div className="journal-proof" aria-hidden="true">
            <span>EDIT / READ / CONNECT</span>
            <i />
            <span>EST. {model.reading.seconds || 30} SEC</span>
          </div>
          {model.sections.length > 0 && (
            <div className="journal-content">
              {model.sections.map((section, index) => (
                <section key={section.id} className="v2-section">
                  {section.kind !== "default" && (
                    <div className="journal-section-head">
                      <span className="journal-section-num" aria-hidden="true">§ {String(index + 1).padStart(2, "0")}</span>
                      <h2>{section.title}</h2>
                    </div>
                  )}
                  <ThemeSectionBody
                    section={section}
                    prefix="journal"
                    entryBadge={(itemIndex) => `NO. ${String(itemIndex + 1).padStart(2, "0")}`}
                    entryLeader
                  />
                </section>
              ))}
            </div>
          )}
          {links.length > 0 && (
            <nav className="journal-links" aria-label={t("theme.connect")}>
              <div className="journal-links-heading">
                <span aria-hidden="true">↳</span>
                <strong>{t("theme.connect")}</strong>
              </div>
              {links.map(link => (
                <ProfileLinkItem key={link.id} link={link} interactiveLinks={interactiveLinks} onLinkClick={onLinkClick} className="journal-link">
                  <span>{link.label}</span>
                  <LinkActionIcon action={resolveProfileLink(link).action} link={link} />
                </ProfileLinkItem>
              ))}
            </nav>
          )}
        </div>
        <ThemeFooter showAttribution={showAttribution} t={t} />
      </div>
    </article>
  );
}

function SignalTheme({ model, interactiveLinks, onLinkClick, showAttribution, t }: ThemeRendererProps) {
  const links = sortProfileLinks(model.profile.links);
  const hasLowerContent = model.sections.length > 0 || links.length > 0;
  const sparseClass = hasLowerContent ? "" : " theme-v2-sparse";

  return (
    <article className={`theme-v2-wrapper theme-v2-signal${sparseClass}`} data-theme="poster">
      <div className="signal-shell">
        <div className="signal-grid" aria-hidden="true" />
        <header className="signal-topline">
          <strong>30s.to/@{model.profile.handle || "handle"}</strong>
          <span>SIGNAL / 30S · LIVE</span>
        </header>
        <div className="signal-stage">
          <section className="signal-hero">
            <span className="signal-marker" aria-hidden="true">01 / ID</span>
            <div className="signal-target" aria-hidden="true"><i /><i /></div>
            <h1>{model.profile.displayName || t("theme.fallback.nameUpper")}</h1>
            <div className="signal-frequency" aria-hidden="true">
              <span>30</span><i /><span>SEC</span>
            </div>
          </section>
          <div className="signal-intro">
            <span className="signal-marker" aria-hidden="true">02 / SIGNAL</span>
            <p>{model.profile.oneLineIdentity || t("theme.fallback.identityUpper")}</p>
            {model.profile.tags.length > 0 && (
              <div className="signal-tags">
                {model.profile.tags.map(tag => <span key={tag}>#{tag}</span>)}
              </div>
            )}
          </div>
        </div>
        {hasLowerContent && (
          <div className="signal-lower">
            {model.sections.length > 0 && (
              <div className="signal-sections">
                {model.sections.map((section, index) => {
                  const layout = analyzeThemeSection(section);
                  const modeLabel = layout.type === "entries" ? "CH / INDEX" : layout.type === "timeline" ? "LOG / TIME" : "BROADCAST";
                  return (
                    <section className="signal-section" key={section.id}>
                      <div className="signal-section-heading">
                        <span>{String(index + 3).padStart(2, "0")}</span>
                        {section.kind !== "default" && <h2>{section.title}</h2>}
                        <i className="signal-section-rule" aria-hidden="true" />
                        <em className="signal-section-mode" aria-hidden="true">{modeLabel}</em>
                      </div>
                      <ThemeSectionBody
                        section={section}
                        prefix="signal"
                        entryBadge={(itemIndex) => `CH.${String(itemIndex + 1).padStart(2, "0")}`}
                      />
                    </section>
                  );
                })}
              </div>
            )}
            {links.length > 0 && (
              <nav className="signal-links" aria-label={t("theme.connect")}>
                <div className="signal-links-heading">
                  <span>{t("theme.connect")}</span>
                  <strong>{String(links.length).padStart(2, "0")}</strong>
                </div>
                {links.map((link, index) => (
                  <ProfileLinkItem key={link.id} link={link} interactiveLinks={interactiveLinks} onLinkClick={onLinkClick} className="signal-link">
                    <small className="signal-link-index" aria-hidden="true">{String(index + 1).padStart(2, "0")}</small>
                    <span>{link.label}</span>
                    <LinkActionIcon action={resolveProfileLink(link).action} link={link} />
                  </ProfileLinkItem>
                ))}
              </nav>
            )}
          </div>
        )}
        <ThemeFooter showAttribution={showAttribution} t={t} />
      </div>
    </article>
  );
}

function MosaicTheme({ model, interactiveLinks, onLinkClick, showAttribution, t }: ThemeRendererProps) {
  const links = sortProfileLinks(model.profile.links);
  const sparseClass = model.sections.length === 0 && links.length === 0 ? " theme-v2-sparse" : "";

  return (
    <article className={`theme-v2-wrapper theme-v2-mosaic${sparseClass}`} data-theme="index">
      <div className="mosaic-shell">
        <div className="mosaic-grid">
          <header className="mosaic-card mosaic-primary">
            <div className="mosaic-card-meta">
              <span>MOSAIC / 30S</span>
              <strong>30s.to/@{model.profile.handle || "handle"}</strong>
            </div>
            <h1>{model.profile.displayName || t("theme.fallback.nameIndex")}</h1>
            <div className="mosaic-orbit" aria-hidden="true"><span /><span /><span /></div>
          </header>
          <div className="mosaic-card mosaic-identity">
            <span className="mosaic-eyebrow">{t("theme.identityLabel")}</span>
            <p>{model.profile.oneLineIdentity || t("theme.fallback.identityIndex")}</p>
            {model.profile.tags.length > 0 && (
              <div className="mosaic-tags">
                {model.profile.tags.map(tag => <span key={tag}>#{tag}</span>)}
              </div>
            )}
            <div className="mosaic-identity-mark" aria-hidden="true">30<br />SEC</div>
          </div>
          {model.sections.map((section, index) => (
            <section
              className={model.sections.length % 2 === 1 && index === model.sections.length - 1
                ? "mosaic-card mosaic-section mosaic-section-wide"
                : "mosaic-card mosaic-section"}
              key={section.id}
            >
              <div className="mosaic-section-heading">
                <span>{String(index + 1).padStart(2, "0")}</span>
                {section.kind !== "default" && <h2>{section.title}</h2>}
                <i className="mosaic-section-dot" aria-hidden="true" />
              </div>
              <ThemeSectionBody
                section={section}
                prefix="mosaic"
                entryBadge={(itemIndex) => String(itemIndex + 1).padStart(2, "0")}
              />
            </section>
          ))}
          {links.length > 0 && (
            <nav className="mosaic-links" aria-label={t("theme.connect")}>
              {links.map((link, index) => (
                <ProfileLinkItem key={link.id} link={link} interactiveLinks={interactiveLinks} onLinkClick={onLinkClick} className="mosaic-card mosaic-link">
                  <small className="mosaic-link-index" aria-hidden="true">{String(index + 1).padStart(2, "0")}</small>
                  <span>{link.label}</span>
                  <LinkActionIcon action={resolveProfileLink(link).action} link={link} />
                </ProfileLinkItem>
              ))}
            </nav>
          )}
        </div>
        <div className="mosaic-footer">
          <ThemeFooter showAttribution={showAttribution} t={t} />
        </div>
      </div>
    </article>
  );
}

function RisoTheme({ model, interactiveLinks, onLinkClick, showAttribution, t }: ThemeRendererProps) {
  const links = sortProfileLinks(model.profile.links);
  const sparseClass = model.sections.length === 0 && links.length === 0 ? " theme-v2-sparse" : "";
  const displayName = model.profile.displayName || t("theme.fallback.name");

  return (
    <article className={`theme-v2-wrapper theme-v2-riso${sparseClass}`} data-theme="riso">
      <div className="riso-sheet">
        <div className="riso-halftone" aria-hidden="true" />
        <header className="riso-header">
          <div className="riso-folio">
            <span>RISO / 30S · EDITION 001</span>
            <strong>30s.to/@{model.profile.handle || "handle"}</strong>
          </div>
          <div className="riso-nameplate">
            <div className="riso-registration" aria-hidden="true"><i /><i /><i /></div>
            <h1 data-print={displayName}>{displayName}</h1>
          </div>
          <div className="riso-intro">
            <span aria-hidden="true">PRINT NOTE / 02</span>
            <p>{model.profile.oneLineIdentity || t("theme.fallback.identity")}</p>
          </div>
          {model.profile.tags.length > 0 && (
            <div className="riso-tags">
              {model.profile.tags.map((tag, index) => <span key={tag} data-index={String(index + 1).padStart(2, "0")}>{tag}</span>)}
            </div>
          )}
        </header>
        <div className="riso-flow">
          {model.sections.length > 0 && (
            <div className="riso-sections">
              {model.sections.map((section, index) => (
                <section className="riso-section" key={section.id}>
                  <div className="riso-section-heading">
                    <span>{String(index + 3).padStart(2, "0")}</span>
                    {section.kind !== "default" && <h2>{section.title}</h2>}
                  </div>
                  <ThemeSectionBody
                    section={section}
                    prefix="riso"
                    entryBadge={(itemIndex) => `PLATE ${String(itemIndex + 1).padStart(2, "0")}`}
                  />
                </section>
              ))}
            </div>
          )}
          {links.length > 0 && (
            <nav className="riso-links" aria-label={t("theme.connect")}>
              <div className="riso-links-heading">
                <span>{t("theme.connect")}</span>
                <strong>{String(links.length).padStart(2, "0")} TICKETS</strong>
              </div>
              <div className="riso-ticket-grid">
                {links.map((link, index) => (
                  <ProfileLinkItem key={link.id} link={link} interactiveLinks={interactiveLinks} onLinkClick={onLinkClick} className="riso-link">
                    <small>{String(index + 1).padStart(2, "0")}</small>
                    <span>{link.label}</span>
                    <LinkActionIcon action={resolveProfileLink(link).action} link={link} />
                  </ProfileLinkItem>
                ))}
              </div>
            </nav>
          )}
        </div>
        <ThemeFooter showAttribution={showAttribution} t={t} />
      </div>
    </article>
  );
}

function PrismTheme({ model, interactiveLinks, onLinkClick, showAttribution, t }: ThemeRendererProps) {
  const links = sortProfileLinks(model.profile.links);
  const sparseClass = model.sections.length === 0 && links.length === 0 ? " theme-v2-sparse" : "";

  return (
    <article className={`theme-v2-wrapper theme-v2-prism${sparseClass}`} data-theme="prism">
      <div className="prism-shell">
        <div className="prism-spectrum" aria-hidden="true"><i /><i /><i /></div>
        <header className="prism-header">
          <div className="prism-meta">
            <strong>30s.to/@{model.profile.handle || "handle"}</strong>
            <span>PRISM / 30S · REFRACT</span>
          </div>
          <div className="prism-hero">
            <div className="prism-lens" aria-hidden="true"><i /><i /></div>
            <div className="prism-identity">
              <span aria-hidden="true">VISIBLE SPECTRUM / 01</span>
              <h1>{model.profile.displayName || t("theme.fallback.name")}</h1>
            </div>
            <div className="prism-intro">
              <span aria-hidden="true">SIGNAL / 02</span>
              <p>{model.profile.oneLineIdentity || t("theme.fallback.identity")}</p>
              {model.profile.tags.length > 0 && (
                <div className="prism-tags">
                  {model.profile.tags.map(tag => <span key={tag}>{tag}</span>)}
                </div>
              )}
            </div>
          </div>
        </header>
        <div className="prism-flow">
          {model.sections.length > 0 && (
            <div className="prism-sections">
              {model.sections.map((section, index) => (
                <section className="prism-section" key={section.id}>
                  <div className="prism-section-heading">
                    <span>{String(index + 1).padStart(2, "0")}</span>
                    {section.kind !== "default" && <h2>{section.title}</h2>}
                  </div>
                  <ThemeSectionBody
                    section={section}
                    prefix="prism"
                    entryBadge={(itemIndex) => String(itemIndex + 1).padStart(2, "0")}
                  />
                </section>
              ))}
            </div>
          )}
          {links.length > 0 && (
            <nav className="prism-links" aria-label={t("theme.connect")}>
              <div className="prism-links-heading">
                <span>{t("theme.connect")}</span>
                <strong>{String(links.length).padStart(2, "0")}</strong>
              </div>
              {links.map((link, index) => (
                <ProfileLinkItem key={link.id} link={link} interactiveLinks={interactiveLinks} onLinkClick={onLinkClick} className="prism-link">
                  <small className="prism-link-index" aria-hidden="true">{String(index + 1).padStart(2, "0")}</small>
                  <span>{link.label}</span>
                  <LinkActionIcon action={resolveProfileLink(link).action} link={link} />
                </ProfileLinkItem>
              ))}
            </nav>
          )}
        </div>
        <ThemeFooter showAttribution={showAttribution} t={t} />
      </div>
    </article>
  );
}

function FieldTheme({ model, interactiveLinks, onLinkClick, showAttribution, t }: ThemeRendererProps) {
  const links = sortProfileLinks(model.profile.links);
  const sparseClass = model.sections.length === 0 && links.length === 0 ? " theme-v2-sparse" : "";

  return (
    <article className={`theme-v2-wrapper theme-v2-field${sparseClass}`} data-theme="field">
      <div className="field-shell">
        <svg className="field-contours" viewBox="0 0 480 360" aria-hidden="true">
          <path d="M46 282C71 205 149 198 174 128C196 65 260 24 331 53C406 83 449 172 409 238C373 296 302 315 247 286C190 256 142 292 89 318" />
          <path d="M77 276C97 222 159 210 190 146C220 86 272 54 328 74C385 95 418 166 389 220C358 277 300 289 251 263C197 235 151 269 107 300" />
          <path d="M112 269C134 231 176 218 207 165C235 116 279 84 326 97C370 110 394 164 371 207C344 257 300 264 257 241C212 216 173 248 140 281" />
        </svg>
        <header className="field-header">
          <div className="field-folio">
            <span>FIELD OBSERVATION / 001</span>
            <strong>30s.to/@{model.profile.handle || "handle"}</strong>
          </div>
          <div className="field-specimen">
            <div className="field-identity">
              <span aria-hidden="true">SPECIMEN / IDENTITY</span>
              <h1>{model.profile.displayName || t("theme.fallback.name")}</h1>
              <p>{model.profile.oneLineIdentity || t("theme.fallback.identity")}</p>
            </div>
            <svg className="field-botanical" viewBox="0 0 180 250" aria-hidden="true">
              <path d="M89 231C91 175 91 119 88 24" />
              <path d="M89 171C65 158 46 137 38 109C65 112 86 132 89 171Z" />
              <path d="M89 132C112 117 128 95 133 68C108 75 91 96 89 132Z" />
              <path d="M88 91C68 79 55 61 51 39C72 44 86 62 88 91Z" />
              <circle cx="89" cy="24" r="5" />
            </svg>
          </div>
          {model.profile.tags.length > 0 && (
            <div className="field-tags">
              {model.profile.tags.map((tag, index) => (
                <span key={tag}><small>{String(index + 1).padStart(2, "0")}</small>{tag}</span>
              ))}
            </div>
          )}
        </header>
        <div className="field-flow">
          {model.sections.length > 0 && (
            <div className="field-notes">
              {model.sections.map((section, index) => {
                const layout = analyzeThemeSection(section);
                const obsType = layout.type === "entries" ? "SPECIMEN LOG" : layout.type === "timeline" ? "CHRONOLOGY" : "FIELD NOTE";
                return (
                  <section className="field-note" key={section.id}>
                    <div className="field-note-heading">
                      <span>OBS. {String(index + 1).padStart(2, "0")} · {obsType}</span>
                      {section.kind !== "default" && <h2>{section.title}</h2>}
                    </div>
                    <ThemeSectionBody
                      section={section}
                      prefix="field"
                      entryBadge={(itemIndex) => `SPECIMEN ${String(itemIndex + 1).padStart(2, "0")}`}
                    />
                  </section>
                );
              })}
            </div>
          )}
          {links.length > 0 && (
            <nav className="field-links" aria-label={t("theme.connect")}>
              <div className="field-links-heading">
                <span>COORDINATES / {t("theme.connect")}</span>
                <strong>{String(links.length).padStart(2, "0")}</strong>
              </div>
              {links.map((link, index) => (
                <ProfileLinkItem key={link.id} link={link} interactiveLinks={interactiveLinks} onLinkClick={onLinkClick} className="field-link">
                  <small>{String(index + 1).padStart(2, "0")}</small>
                  <span>{link.label}</span>
                  <LinkActionIcon action={resolveProfileLink(link).action} link={link} />
                </ProfileLinkItem>
              ))}
            </nav>
          )}
        </div>
        <ThemeFooter showAttribution={showAttribution} t={t} />
      </div>
    </article>
  );
}

function NoirTheme({ model, interactiveLinks, onLinkClick, showAttribution, t }: ThemeRendererProps) {
  const links = sortProfileLinks(model.profile.links);
  const sparseClass = model.sections.length === 0 && links.length === 0 ? " theme-v2-sparse" : "";
  const rootRef = useNoirPagination();

  return (
    <article ref={rootRef} className={`theme-v2-wrapper theme-v2-noir${sparseClass}`} data-theme="noir">
      <div className="noir-grain" aria-hidden="true" />
      <div className="noir-scroller">
        <section className="noir-page noir-hero-page">
          <header className="noir-hero">
            <div className="noir-handle">30s.to/@{model.profile.handle || "handle"}</div>
            <h1>{model.profile.displayName || t("theme.fallback.nameNoir")}</h1>
            <p>{model.profile.oneLineIdentity || t("theme.fallback.identityNoir")}</p>
            {model.profile.tags.length > 0 && (
              <div className="noir-tags">
                {model.profile.tags.map(tag => <span key={tag}>{tag}</span>)}
              </div>
            )}
          </header>
          <div className="noir-scroll-indicator">
            <span>{t("theme.scroll")}</span>
            <div className="noir-scroll-line" />
          </div>
        </section>

        {model.sections.map((section, index) => (
          <section key={section.id} className="noir-page noir-content-page">
            <div className="noir-content">
              <section className="v2-section">
                <div className="noir-frame-meta" aria-hidden="true">
                  <span>FRAME {String(index + 1).padStart(2, "0")} / {String(model.sections.length).padStart(2, "0")}</span>
                  <span>2.39:1 · 35MM</span>
                </div>
                {section.kind !== "default" && <h2>{section.title}</h2>}
                <ThemeSectionBody
                  section={section}
                  prefix="noir"
                  entryBadge={(itemIndex) => `REEL ${String(itemIndex + 1).padStart(2, "0")}`}
                />
              </section>
            </div>
          </section>
        ))}

        {links.length > 0 && (
          <section className="noir-page noir-links-page">
            <div className="noir-links-container">
              <h2 className="noir-links-title">{t("theme.connect")}</h2>
              <nav className="noir-links" aria-label={t("theme.connect")}>
                {links.map((link, index) => (
                  <ProfileLinkItem key={link.id} link={link} interactiveLinks={interactiveLinks} onLinkClick={onLinkClick} className="noir-link">
                    <small className="noir-link-index" aria-hidden="true">{String(index + 1).padStart(2, "0")}</small>
                    <span>{link.label}</span>
                    <LinkActionIcon action={resolveProfileLink(link).action} link={link} />
                  </ProfileLinkItem>
                ))}
              </nav>
            </div>
          </section>
        )}
        <ThemeFooter showAttribution={showAttribution} t={t} />
      </div>
    </article>
  );
}

function useNoirPagination() {
  const rootRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    root.dataset.paginationReady = "true";

    const previewViewport = root.closest<HTMLElement>(".preview-page-viewport");
    const wheelTarget = previewViewport ?? root;
    let wheelDelta = 0;
    let wheelGestureHandled = false;
    let wheelIdleTimer: number | undefined;

    const getViewportHeight = () => previewViewport?.clientHeight ?? window.innerHeight;
    const getScrollTop = () => previewViewport?.scrollTop ?? window.scrollY;
    const getMaxScroll = () => previewViewport
      ? Math.max(0, previewViewport.scrollHeight - previewViewport.clientHeight)
      : Math.max(0, document.documentElement.scrollHeight - window.innerHeight);

    const getLayoutTop = (element: HTMLElement) => {
      let top = 0;
      let current: HTMLElement | null = element;
      while (current) {
        top += current.offsetTop;
        current = current.offsetParent as HTMLElement | null;
      }
      return top;
    };

    const getPageTop = (page: HTMLElement) => {
      const pageTop = getLayoutTop(page);
      return previewViewport ? pageTop - getLayoutTop(previewViewport) : pageTop;
    };

    const getPageTargets = () => {
      const viewportHeight = getViewportHeight();
      const maxScroll = getMaxScroll();
      const targets: number[] = [0, maxScroll];

      root.querySelectorAll<HTMLElement>(".noir-page").forEach((page) => {
        const pageTop = getPageTop(page);
        const pageHeight = page.getBoundingClientRect().height;
        const lastViewportTop = Math.min(maxScroll, Math.max(pageTop, pageTop + pageHeight - viewportHeight));
        targets.push(pageTop);

        if (!page.classList.contains("noir-hero-page")) {
          for (let target = pageTop + viewportHeight; target < lastViewportTop - 1; target += viewportHeight) {
            targets.push(target);
          }
          if (lastViewportTop > pageTop + 1) targets.push(lastViewportTop);
        }
      });

      return [...new Set(targets.map((target) => Math.round(Math.min(maxScroll, Math.max(0, target)))))]
        .sort((a, b) => a - b);
    };

    const scrollToTarget = (target: number) => {
      const behavior: ScrollBehavior = window.matchMedia("(prefers-reduced-motion: reduce)").matches
        ? "auto"
        : "smooth";
      if (previewViewport) {
        previewViewport.scrollTo({ top: target, behavior });
      } else {
        window.scrollTo({ top: target, behavior });
      }
    };

    const movePage = (direction: -1 | 1) => {
      const current = getScrollTop();
      const targets = getPageTargets();
      const tolerance = 4;
      const target = direction > 0
        ? targets.find((value) => value > current + tolerance)
        : [...targets].reverse().find((value) => value < current - tolerance);
      if (target !== undefined) scrollToTarget(target);
    };

    const resetWheelGesture = () => {
      wheelGestureHandled = false;
      wheelDelta = 0;
    };

    const shouldChainAtEdge = (direction: number) => {
      if (!previewViewport) return false;
      const atEdge = direction < 0 ? getScrollTop() <= 1 : getScrollTop() >= getMaxScroll() - 1;
      // Respect the embedding surface's scroll policy, including responsive changes.
      return atEdge && getComputedStyle(previewViewport).overscrollBehaviorY === "auto";
    };

    const handleWheel = (event: WheelEvent) => {
      if (event.ctrlKey || Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;
      if (shouldChainAtEdge(event.deltaY)) {
        window.clearTimeout(wheelIdleTimer);
        resetWheelGesture();
        return;
      }
      event.preventDefault();
      event.stopPropagation();

      window.clearTimeout(wheelIdleTimer);
      wheelIdleTimer = window.setTimeout(resetWheelGesture, 180);
      if (wheelGestureHandled) return;

      wheelDelta += event.deltaY;
      if (Math.abs(wheelDelta) < 24) return;
      wheelGestureHandled = true;
      movePage(wheelDelta > 0 ? 1 : -1);
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey) return;
      const target = event.target as HTMLElement | null;
      if (target?.closest("input, textarea, select, [contenteditable='true']")) return;
      if (previewViewport && !previewViewport.contains(document.activeElement)) return;
      if (target?.closest("a, button") && (event.key === " " || event.key === "Enter")) return;

      let direction: -1 | 1 | undefined;
      if (event.key === "PageDown" || event.key === "ArrowDown" || (event.key === " " && !event.shiftKey)) direction = 1;
      if (event.key === "PageUp" || event.key === "ArrowUp" || (event.key === " " && event.shiftKey)) direction = -1;

      if (event.key === "Home") {
        event.preventDefault();
        scrollToTarget(0);
        return;
      }
      if (event.key === "End") {
        event.preventDefault();
        scrollToTarget(getMaxScroll());
        return;
      }
      if (!direction) return;
      if (shouldChainAtEdge(direction)) return;
      event.preventDefault();
      movePage(direction);
    };

    wheelTarget.addEventListener("wheel", handleWheel, { passive: false });
    if (previewViewport) {
      previewViewport.addEventListener("keydown", handleKeyDown);
    } else {
      window.addEventListener("keydown", handleKeyDown);
    }
    return () => {
      delete root.dataset.paginationReady;
      wheelTarget.removeEventListener("wheel", handleWheel);
      if (previewViewport) {
        previewViewport.removeEventListener("keydown", handleKeyDown);
      } else {
        window.removeEventListener("keydown", handleKeyDown);
      }
      window.clearTimeout(wheelIdleTimer);
    };
  }, []);

  return rootRef;
}

function TerminalTheme({ model, interactiveLinks, onLinkClick, showAttribution, t }: ThemeRendererProps) {
  const links = sortProfileLinks(model.profile.links);
  const sparseClass = model.sections.length === 0 && links.length === 0 ? " theme-v2-sparse" : "";

  return (
    <article className={`theme-v2-wrapper theme-v2-terminal${sparseClass}`} data-theme="terminal">
      <div className="terminal-texture" aria-hidden="true" />
      <div className="term-window">
        <div className="term-titlebar">
          <div className="term-buttons">
            <span className="tb-close" />
            <span className="tb-min" />
            <span className="tb-max" />
          </div>
          <div className="term-title">30s.to/@{model.profile.handle || "handle"}</div>
        </div>
        <div className="term-body">
          <div className="term-block">
            <div className="term-prompt"><span>~ $</span> whoami</div>
            <h1 className="term-output term-name">{model.profile.displayName || t("theme.fallback.terminalName")}</h1>
          </div>
          <div className="term-block">
            <div className="term-prompt"><span>~ $</span> intro</div>
            <div className="term-output">{model.profile.oneLineIdentity || t("theme.fallback.terminalIntro")}</div>
          </div>
          {model.profile.tags.length > 0 && (
            <div className="term-block">
              <div className="term-prompt"><span>~ $</span> tags</div>
              <ul className="term-output term-tag-list">
                {model.profile.tags.map(tag => <li key={tag}>{tag}</li>)}
              </ul>
            </div>
          )}
          
          {model.sections.map(sec => (
            <div className="term-block" data-terminal-section key={sec.id}>
              <h2 className="term-prompt"><span>~ $</span> cat {sec.title.toLowerCase().replace(/\s+/g, '_')}.md</h2>
              <ThemeSectionBody
                section={sec}
                prefix="term"
                proseClassName="term-output"
                entryBadge={(itemIndex) => `[${String(itemIndex + 1).padStart(2, "0")}]`}
                showEntryHrefPreview
              />
            </div>
          ))}

          {links.length > 0 && (
            <div className="term-block">
              <div className="term-prompt"><span>~ $</span> tree -a ./links</div>
              <TerminalLinksTree
                interactiveLinks={interactiveLinks}
                links={links}
                onLinkClick={onLinkClick}
                t={t}
              />
            </div>
          )}
          {showAttribution && (
            <footer className="term-block term-attribution">
              <div className="term-prompt"><span>~ $</span> cat /etc/motd</div>
              <div className="term-output">
                Welcome to 30s.to/@{model.profile.handle || "handle"} (<ThemeAttribution t={t} />)
              </div>
            </footer>
          )}
          <TerminalSession
            interactiveLinks={interactiveLinks}
            links={links}
            model={model}
            onLinkClick={onLinkClick}
            showAttribution={showAttribution}
            t={t}
          />
        </div>
      </div>
    </article>
  );
}
